import { LOCATIONS_SEARCH_EXPERIMENT } from '@/constants/experimentConstants';
import { getGeoLocation, loggedUser } from '@/helpers';
import { useAppSelector } from '@/hooks';
import { useGetAmplitudeExperimentVariant } from '@/hooks/useAmplitudeExperiment';
import { useEffect, useReducer, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import {
  clearLastLocationSuggestionFromLocalStorage,
  getLastLocationSuggestionFromLocalStorage,
  getLocationSuggestionFromSearchHistory,
  getSuggestionsFromSearchValue,
  SESSION_STORAGE_KEY_NO_DEFAULT_LOCATION,
} from './LocationAutoSuggest.helpers';
import {
  LocationAutoSuggestAction,
  LocationAutoSuggestManagerProps,
  LocationAutoSuggestState,
  LocationSuggestion,
} from './LocationAutoSuggest.types';

const locationAutosuggestReducer = (
  state: LocationAutoSuggestState,
  action: LocationAutoSuggestAction,
): LocationAutoSuggestState => {
  switch (action.type) {
    case 'ON_INPUT_CHANGE': {
      const { value, lat, lon } = action.payload;
      if (action.payload.value === '') {
        sessionStorage.setItem(SESSION_STORAGE_KEY_NO_DEFAULT_LOCATION, 'true');
      } else {
        sessionStorage.removeItem(SESSION_STORAGE_KEY_NO_DEFAULT_LOCATION);
      }
      return { ...state, locationValue: value, lat, lon };
    }

    case 'ON_SUGGESTION_LOADED': {
      return { ...state, ...action.payload };
    }

    case 'SET_SHOW_SUGGESTIONS': {
      return { ...state, showSuggestions: action.payload };
    }

    case 'FETCHING_USER_POSITION': {
      return { ...state, isFetchingUserPosition: action.payload };
    }
    default:
      return state;
  }
};

const useLocationAutoSuggestManager = ({
  initialState,
  loadFromHistory = true,
  onSelectLocationSuggestion,
}: LocationAutoSuggestManagerProps) => {
  const [state, dispatch] = useReducer(locationAutosuggestReducer, initialState);
  const abortControllerRef = useRef<AbortController | null>(null);
  const pendingValueRef = useRef<string | null>(initialState.locationValue);
  const isFetchingRef = useRef(false);
  const locationState = useLocation();

  const flags = useAppSelector((state) => state.flags);
  const getVariant = useGetAmplitudeExperimentVariant();
  const locationSource = flags[LOCATIONS_SEARCH_EXPERIMENT]?.value ?? getVariant(LOCATIONS_SEARCH_EXPERIMENT)?.value;

  useEffect(() => {
    async function initLocationValueFromHistory() {
      const noDefaultLocation = sessionStorage.getItem(SESSION_STORAGE_KEY_NO_DEFAULT_LOCATION);

      if (initialState.locationValue || noDefaultLocation || !loadFromHistory) {
        return;
      }

      let suggestion = undefined;

      const locationSuggestionFromLocal = getLastLocationSuggestionFromLocalStorage();

      if (locationSuggestionFromLocal) {
        suggestion = locationSuggestionFromLocal;
      } else if (loggedUser()) {
        suggestion = await getLocationSuggestionFromSearchHistory();
      }

      if (!suggestion) {
        return;
      }
      dispatch({ type: 'ON_INPUT_CHANGE', payload: { value: suggestion.label } });

      const params: { location: string; lat?: string; lon?: string; searchContext?: string } = {
        location: suggestion.label,
        /**
         * To avoid resetting the searchContext when this is called programatically
         * which will trigger a new search
         */
        searchContext: locationState?.state?.searchContext,
      };

      if (suggestion.position?.lat && suggestion.position?.lon) {
        params.lat = suggestion.position.lat;
        params.lon = suggestion.position.lon;
      }

      onSelectLocationSuggestion(params);
    }
    initLocationValueFromHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleOnChange = (event) => {
    /**
     * Clear lat/lon when user types in the input
     * If user clicks on a suggestion, the lat/lon will be set
     * due to autosuggest onSuggestionSelected callback is called
     */
    const payload = {
      value: event.target.value,
      lat: undefined,
      lon: undefined,
    };

    dispatch({ type: 'ON_INPUT_CHANGE', payload });
  };

  const handleOnFocus = () => {
    dispatch({ type: 'SET_SHOW_SUGGESTIONS', payload: true });
  };

  const handleOnBlur = (_, { highlightedSuggestion }) => {
    /**
     * if the input is blurred, make sure to abort any ongoing fetch requests
     */
    abortControllerRef.current?.abort();
  };
  const handleOnKeyDown = (event: React.KeyboardEvent) => {};

  const handleOnSuggestionsFetchRequested = async ({ value, reason }) => {
    if (reason !== 'input-focused' && reason !== 'input-changed') {
      return;
    }

    pendingValueRef.current = value;

    if (isFetchingRef.current) {
      return;
    }

    const fetchSuggestions = async (input: string) => {
      isFetchingRef.current = true;

      if (abortControllerRef.current?.signal?.aborted || !abortControllerRef.current) {
        abortControllerRef.current = new AbortController();
      }

      const signal = abortControllerRef.current.signal;

      const { hasHistory, storedSuggestions, suggestions } = await getSuggestionsFromSearchValue(
        input,
        signal,
        locationSource,
      );

      isFetchingRef.current = false;

      dispatch({ type: 'ON_SUGGESTION_LOADED', payload: { suggestions, storedSuggestions, hasHistory } });

      if (
        pendingValueRef.current !== null &&
        pendingValueRef?.current !== input &&
        !abortControllerRef.current?.signal?.aborted
      ) {
        fetchSuggestions(pendingValueRef.current);
        pendingValueRef.current = null;
      }
    };

    fetchSuggestions(value);
  };

  const handleOnSuggestionsClearRequested = (props) => {
    abortControllerRef.current?.abort?.();
    dispatch({ type: 'ON_SUGGESTION_LOADED', payload: { suggestions: [], storedSuggestions: [], hasHistory: false } });
  };

  const handleOnClearHistorySuggestion = (suggestion: LocationSuggestion) => {
    const HISTORY = 'history';
    const historyIndex = state.storedSuggestions.findIndex((suggestion) => suggestion.type === HISTORY);
    const currentHistorySuggestions = state.storedSuggestions[historyIndex];
    const updatedHistorySuggestions = currentHistorySuggestions.suggestions.filter(
      (currentSuggestion) => currentSuggestion.label !== suggestion.label,
    );

    const historySuggestions = updatedHistorySuggestions ?? [];
    const nonHistorySuggestions = state.suggestions.filter((suggestion) => suggestion.type !== HISTORY);

    const payload = {
      suggestions: [
        {
          suggestions: historySuggestions?.slice(0, 3),
          title: HISTORY,
          type: HISTORY,
        },
        ...nonHistorySuggestions,
      ],
      storedSuggestions: [
        {
          suggestions: historySuggestions,
          title: HISTORY,
          type: HISTORY,
        },
        ...nonHistorySuggestions,
      ],
      hasHistory: !!historySuggestions.length,
    };

    dispatch({ type: 'ON_SUGGESTION_LOADED', payload });
  };

  const handleOnUpdateUserLocation = (
    params: { location: string; lat?: string; lon?: string },
    suggestionValue: string,
  ) => {
    const { lat, lon } = params;
    const payload = { value: suggestionValue, lat, lon };
    dispatch({ type: 'ON_INPUT_CHANGE', payload });
    dispatch({ type: 'SET_SHOW_SUGGESTIONS', payload: false });
    dispatch({ type: 'FETCHING_USER_POSITION', payload: false });
    onSelectLocationSuggestion(params);
  };

  const handleOnFetchAndSetUserLocation = async (suggestionValue: string) => {
    dispatch({ type: 'FETCHING_USER_POSITION', payload: false });

    getGeoLocation(
      (lat: string, lon: string) => {
        const params = { location: suggestionValue, undefined, lat, lon };
        clearLastLocationSuggestionFromLocalStorage();
        handleOnUpdateUserLocation(params, suggestionValue);
      },
      (cached) => {},
      () => {
        // on loading
        dispatch({ type: 'FETCHING_USER_POSITION', payload: true });
      },
      () => {
        // on error
        dispatch({ type: 'FETCHING_USER_POSITION', payload: false });
      },
    );
  };

  const handleOnSuggestionSelected = async (event, { suggestion, suggestionValue }) => {
    if (event?.target?.id === 'remove_suggested') {
      // @TODO: do stuff
      return;
    }

    const params: { location: string; lat?: string; lon?: string } = {
      location: suggestion.label,
    };

    if (suggestion.type === 'geolocation') {
      dispatch({ type: 'ON_INPUT_CHANGE', payload: { value: suggestionValue } });
      handleOnFetchAndSetUserLocation(suggestionValue);
      return;
    }

    if (suggestion.position?.lat && suggestion.position?.lon) {
      params.lat = suggestion.position.lat;
      params.lon = suggestion.position.lon;
    }

    clearLastLocationSuggestionFromLocalStorage();

    handleOnUpdateUserLocation(params, suggestionValue);
  };

  return {
    location: state.locationValue,
    lat: state.lat,
    lon: state.lon,
    suggestions: state.suggestions,
    storedSuggestions: state.storedSuggestions,
    isFetchingUserPosition: state.isFetchingUserPosition || locationState?.state?.isFetchingUserPosition,
    showSuggestions: state.showSuggestions,
    onChange: handleOnChange,
    onBlur: handleOnBlur,
    onFocus: handleOnFocus,
    onKeyDown: handleOnKeyDown,
    onClick: handleOnFocus,
    onSuggestionsFetchRequested: handleOnSuggestionsFetchRequested,
    onSuggestionsClearRequested: handleOnSuggestionsClearRequested,
    onSuggestionSelected: handleOnSuggestionSelected,
    onClearHistorySuggestion: handleOnClearHistorySuggestion,
    onFetchAndSetUserLocation: handleOnFetchAndSetUserLocation,
  };
};

export default useLocationAutoSuggestManager;
